import { useEffect, useRef } from 'react';

interface ChatContextMenuProps {
  isPinned: boolean;
  canRename: boolean;
  projectId?: string | null;
  projectName?: string | null;
  onPin: () => void;
  onUnpin: () => void;
  onRename: () => void;
  onDelete: () => void;
  onMoveToProject?: () => void;
  onChangeProject?: () => void;
  onRemoveFromProject?: () => void;
  onClose: () => void;
}

export function ChatContextMenu({ isPinned, canRename, projectId, projectName: _projectName, onPin, onUnpin, onRename, onDelete, onMoveToProject, onChangeProject, onRemoveFromProject, onClose }: ChatContextMenuProps) {
  void _projectName; // Available for future use (e.g., in confirmation dialogs)
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div className="chat-ctx-menu" ref={ref}>
      <button
        className="chat-ctx-menu-item"
        type="button"
        onClick={(e) => { e.stopPropagation(); if (isPinned) { onUnpin(); } else { onPin(); } onClose(); }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="17" x2="12" y2="22"/>
          <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
        </svg>
        {isPinned ? 'Unpin' : 'Pin'}
      </button>

      <button
        className={`chat-ctx-menu-item ${canRename ? '' : 'disabled'}`}
        type="button"
        onClick={(e) => { e.stopPropagation(); if (canRename) { onRename(); onClose(); } }}
        title={canRename ? undefined : 'Only the chat creator can rename'}
        style={canRename ? undefined : { cursor: 'not-allowed', opacity: 0.5 }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
        Rename
      </button>

      {!projectId && onMoveToProject && (
        <button
          className="chat-ctx-menu-item"
          type="button"
          onClick={(e) => { e.stopPropagation(); onMoveToProject(); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          Add to project
        </button>
      )}

      {projectId && onChangeProject && (
        <button
          className="chat-ctx-menu-item"
          type="button"
          onClick={(e) => { e.stopPropagation(); onChangeProject(); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            <path d="M12 11v6M9 14l3-3 3 3" />
          </svg>
          Change project
        </button>
      )}

      {projectId && onRemoveFromProject && (
        <button
          className="chat-ctx-menu-item"
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemoveFromProject(); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            <line x1="9" y1="14" x2="15" y2="14" />
          </svg>
          Remove from project
        </button>
      )}

      <div className="chat-ctx-menu-divider" />

      <button
        className="chat-ctx-menu-item destructive"
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); onClose(); }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
        Delete
      </button>
    </div>
  );
}
